import React, { useEffect, useState } from 'react'
import { Prompt, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { Action, Location } from 'history'
import { createContextWithHook } from '@chilipiper/utils'
import {
  useGuestSession,
  useQueryParams,
  useAvailabilityContext,
} from '@chilipiper/booking-flow-legacy'
import { injectCustomCss } from '@chilipiper/meetings-components'
import { defaultFetchLocale, LocaleProvider } from '@chilipiper/locale'
import { getSessionId } from '@chilipiper/service/src/session'
import { useEnabledLanguages } from '../../locale'
import { useNavigation, useRouterWillLeave } from './hooks/useNavigation'
import { useFault } from './hooks/useFault'
import { useSingleUseLinkContext } from './singleUseLinkContext'

import './guest-layout.css'
import { GuestContext, GuestContextProviderProps } from './types'
import { GuestWrapper } from './GuestWrapper'
import { inIframe } from './helpers'
import { useFireRedirect } from './hooks/useFireRedirect'

export const { useGuestContext, GuestBaseProvider, withGuestContext } =
  createContextWithHook<GuestContext>()('Guest')

export const GuestProvider = ({ children }: GuestContextProviderProps) => {
  const { singleUseLinkData, isSingleUseLinkExpired, isSingleUseLinkBooked } =
    useSingleUseLinkContext()

  const router = useRouteMatch()
  const location = useLocation()
  const history = useHistory()
  const [splat] = Object.values<string>(router.params)
  const hash = location?.hash?.replace('#', '')

  const [closeButtonVisible, setCloseButtonVisible] = useState(true)
  const { availabilityState } = useAvailabilityContext()
  const loadingError = availabilityState.endpoint !== undefined
  const [autoDial, setAutoDial] = useState(false)
  const {
    marketing,
    footer,
    intercomData,
    processRedirect,
    closeRedirect,
    seriesId,
    routeId,
    hideCloseButton,
    tenantDomain,
    isIntermediate,
    setHideCloseButton,
    setCloseRedirect,
  } = useQueryParams(location)

  const { search, uri, assignee, queue, view, component, setComponent } = useNavigation(
    splat,
    isIntermediate,
    singleUseLinkData
  )
  const { fault } = useFault(uri, history)

  const {
    newSessionCreated,
    createNewSession,
    templateId,
    meetingTypeSlug,
    cancelled,
    loading,
    navigatedError,
    isPersonalWorkspace,
    queueAlgorithm,
    formId,
    setLoading,
  } = useGuestSession(tenantDomain, view, assignee, search, queue, singleUseLinkData, hash)

  const sessionId = getSessionId()

  const { enabledLanguages } = useEnabledLanguages({ templateId })

  const { location: routerLocation, routerWillLeave } = useRouterWillLeave(
    processRedirect,
    closeRedirect,
    autoDial,
    setComponent,
    location
  )

  const isInIframe = inIframe()
  const bookedOrRequested = ['booked', 'requested'].includes(component)

  useEffect(() => {
    if (isIntermediate) {
      injectCustomCss(tenantDomain, routeId, () => setLoading(false))
    } else if (tenantDomain && !sessionId) {
      createNewSession()
    }
  }, [
    createNewSession,
    isIntermediate,
    routeId,
    loading,
    setLoading,
    assignee,
    tenantDomain,
    sessionId,
  ])
  const { isLoading: isLoadingRedirect } = useFireRedirect(
    loading,
    isPersonalWorkspace,
    meetingTypeSlug,
    queueAlgorithm,
    formId
  )

  return (
    <GuestBaseProvider
      value={{
        uri,
        intercomData,
        setCloseRedirect,
        setAutoDial,
        meetingSeriesId: seriesId,
        location: routerLocation,
        hideClose: setHideCloseButton,
        fault,
        isInIframe,
        singleUseLinkData,
        marketing,
        router,
        footer,
        bookedOrRequested,
        processRedirect,
        closeRedirect,
        component,
        hideCloseButton,
        loadingError,
        loading: isLoadingRedirect,
        cancelled,
        navigatedError,
        isSingleUseLinkExpired,
        isSingleUseLinkBooked,
        isIntermediate,
        closeButtonVisible,
        setCloseButtonVisible,
        createNewSession: !newSessionCreated ? createNewSession : undefined,
      }}
    >
      <LocaleProvider fetchLocale={defaultFetchLocale} enabledLanguages={enabledLanguages}>
        <Prompt
          message={(nextLocation: Location, action: Action) => {
            routerWillLeave(nextLocation, action)
            return true
          }}
        />
        <GuestWrapper>{children}</GuestWrapper>
      </LocaleProvider>
    </GuestBaseProvider>
  )
}
